/**
 * WORLD neon sign — "THE BLUE DAHLIA" over the bar door.
 * Letterforms drawn as stroked tube cores (hot thin line) inside a saturated
 * blue halo so bloom produces colored halation around visible tube structure,
 * never a filled slab. Emissive plane flagged `userData.bloom = true` for
 * RENDER's selective bloom; the emissive flickers like a dying sign and one
 * letter (the U in BLUE) randomly drops out for 80–150 ms every 4–9 s.
 * A glossy "wet sheen" strip under the sign lets the neon color smear down
 * the rain-slick brick. LIGHT owns the actual neon light sources.
 */
import {
  BoxGeometry,
  CanvasTexture,
  Group,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  SRGBColorSpace
} from 'three'
import {
  COLOR_DARK_METAL,
  FACADE_FRONT_X,
  NEON_COLOR,
  NEON_EMISSIVE_INTENSITY,
  NEON_SIGN_HEIGHT,
  NEON_SIGN_TEXT,
  NEON_SIGN_WIDTH,
  NEON_SIGN_Y,
  BAR_DOOR_Z
} from './constants'
import { enableShadows, tagBloom, tagSolid } from './helpers'
import { createNightEnvMap } from './textures'

const SIGN_CANVAS_WIDTH = 1024
const SIGN_CANVAS_HEIGHT = 256
const SIGN_BACKING_DEPTH = 0.22
const SIGN_FACE_OFFSET = 0.13
/** Halo pass: blue glow around the tube line. */
const SIGN_HALO_BLUR = 22
const SIGN_HALO_LINE_PX = 11
/** Core pass: hot thin line — the visible neon tube (bolder so the
 * double-line letterforms survive the bloom at distance). */
const SIGN_CORE_BLUR = 4
const SIGN_CORE_LINE_PX = 4

// --- Dying-sign flicker (deterministic, frame-rate independent) --------------
const FLICKER_MIN = 0.86
const FLICKER_BUZZ_HZ = 41.0
const FLICKER_SWELL_HZ = 2.3
/** Random full dropouts every ~8–15 s, each ~120 ms. */
const DROPOUT_SEGMENT_SECONDS = 0.12
const DROPOUT_PROBABILITY = 0.012
const DROPOUT_LEVEL = 0.08

// --- Single-letter dropout (the U in BLUE dims to 20%) -----------------------
/** Index into NEON_SIGN_TEXT of the dying letter. */
const DEAD_LETTER_INDEX = NEON_SIGN_TEXT.indexOf('U')
const DEAD_LETTER_ALPHA = 0.2
const LETTER_DROP_MIN_INTERVAL_S = 4
const LETTER_DROP_MAX_INTERVAL_S = 9
const LETTER_DROP_MIN_DURATION_S = 0.08
const LETTER_DROP_MAX_DURATION_S = 0.15

// --- Wet brick sheen under the sign ------------------------------------------
const SHEEN_WIDTH = 7
const SHEEN_HEIGHT = 2.6
const SHEEN_OPACITY = 0.5
const SHEEN_ROUGHNESS = 0.16
const SHEEN_METALNESS = 0.85

const HASH_SEED_A = 12.9898
const HASH_SEED_B = 43758.5453

const hash = (n: number): number => {
  const value = Math.sin(n * HASH_SEED_A) * HASH_SEED_B
  return value - Math.floor(value)
}

const flickerLevel = (time: number): number => {
  const segment = Math.floor(time / DROPOUT_SEGMENT_SECONDS)
  if (hash(segment) < DROPOUT_PROBABILITY) return DROPOUT_LEVEL
  const span = 1 - FLICKER_MIN
  const buzz = Math.sin(time * FLICKER_BUZZ_HZ) * 0.35
  const swell = Math.sin(time * FLICKER_SWELL_HZ) * 0.65
  return 1 - span * 0.5 * (1 + buzz + swell) * 0.5
}

const SIGN_FONT = 'italic bold 104px Georgia, serif'

/**
 * Draws the neon text letter-by-letter so a single letter can be dimmed.
 * `dimIndex` of -1 draws the healthy sign.
 */
const drawNeonText = (context: CanvasRenderingContext2D, dimIndex: number): void => {
  context.fillStyle = '#04060a'
  context.fillRect(0, 0, SIGN_CANVAS_WIDTH, SIGN_CANVAS_HEIGHT)
  context.font = SIGN_FONT
  context.textAlign = 'left'
  context.textBaseline = 'middle'
  const totalWidth = context.measureText(NEON_SIGN_TEXT).width
  const centerY = SIGN_CANVAS_HEIGHT / 2
  let cursorX = (SIGN_CANVAS_WIDTH - totalWidth) / 2

  const characters = Array.from(NEON_SIGN_TEXT)
  characters.forEach((character, index) => {
    context.globalAlpha = index === dimIndex ? DEAD_LETTER_ALPHA : 1
    // Pass 1 — saturated blue halo around the tube path.
    context.shadowColor = '#1f8fff'
    context.shadowBlur = SIGN_HALO_BLUR
    context.strokeStyle = '#3da9ff'
    context.lineWidth = SIGN_HALO_LINE_PX
    context.strokeText(character, cursorX, centerY)
    // Pass 2 — the hot tube core: a crisp bolder line, blue-tinted white.
    context.shadowColor = '#9fd6ff'
    context.shadowBlur = SIGN_CORE_BLUR
    context.strokeStyle = '#dceeff'
    context.lineWidth = SIGN_CORE_LINE_PX
    context.strokeText(character, cursorX, centerY)
    cursorX += context.measureText(character).width
  })
  context.globalAlpha = 1
}

const createNeonTextTexture = (dimIndex: number): CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = SIGN_CANVAS_WIDTH
  canvas.height = SIGN_CANVAS_HEIGHT
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('WORLD: 2d canvas context unavailable for neon sign')
  }
  drawNeonText(context, dimIndex)
  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  return texture
}

/** Glossy dark strip just proud of the brick — the neon smears down it. */
const createWetSheen = (): Mesh => {
  const sheen = new Mesh(
    new PlaneGeometry(SHEEN_WIDTH, SHEEN_HEIGHT),
    new MeshStandardMaterial({
      color: 0x05070b,
      transparent: true,
      opacity: SHEEN_OPACITY,
      roughness: SHEEN_ROUGHNESS,
      metalness: SHEEN_METALNESS,
      envMap: createNightEnvMap(),
      envMapIntensity: 1.2
    })
  )
  sheen.rotation.y = -Math.PI / 2
  return sheen
}

export interface NeonSignBuild {
  readonly group: Group
  /** Advance the dying-sign emissive flicker. */
  update(dt: number): void
}

export const buildNeonSign = (): NeonSignBuild => {
  const group = new Group()
  group.name = 'neon-sign'

  const backing = new Mesh(
    new BoxGeometry(SIGN_BACKING_DEPTH, NEON_SIGN_HEIGHT + 0.2, NEON_SIGN_WIDTH + 0.3),
    new MeshStandardMaterial({ color: COLOR_DARK_METAL, roughness: 0.5, metalness: 0.6 })
  )
  enableShadows(backing)
  tagSolid(backing)
  group.add(backing)

  const healthyTexture = createNeonTextTexture(-1)
  const deadLetterTexture = createNeonTextTexture(DEAD_LETTER_INDEX)
  const faceMaterial = new MeshStandardMaterial({
    color: 0x000000,
    emissive: NEON_COLOR,
    emissiveMap: healthyTexture,
    emissiveIntensity: NEON_EMISSIVE_INTENSITY,
    roughness: 0.4,
    metalness: 0
  })
  const face = new Mesh(new PlaneGeometry(NEON_SIGN_WIDTH, NEON_SIGN_HEIGHT), faceMaterial)
  face.rotation.y = -Math.PI / 2
  face.position.x = -SIGN_FACE_OFFSET
  tagBloom(face)
  group.add(face)

  // Wet brick under the sign — hangs just proud of the facade, spanning from
  // the sign's lower edge down toward the bar door so the blue smears down.
  const sheen = createWetSheen()
  const sheenDropFromSignCenter = NEON_SIGN_HEIGHT / 2 + SHEEN_HEIGHT / 2 + 0.1
  sheen.position.set(-SIGN_FACE_OFFSET + 0.05, -sheenDropFromSignCenter, 0)
  group.add(sheen)

  group.position.set(FACADE_FRONT_X - SIGN_BACKING_DEPTH / 2, NEON_SIGN_Y, BAR_DOOR_Z)

  let time = 0
  let nextLetterDropAt =
    LETTER_DROP_MIN_INTERVAL_S +
    hash(1) * (LETTER_DROP_MAX_INTERVAL_S - LETTER_DROP_MIN_INTERVAL_S)
  let letterDropEndsAt = -1
  let dropSeed = 2

  const update = (dt: number): void => {
    time += dt
    faceMaterial.emissiveIntensity = NEON_EMISSIVE_INTENSITY * flickerLevel(time)

    // Single-letter dropout state machine (deterministic via hashed seeds).
    if (letterDropEndsAt < 0 && time >= nextLetterDropAt) {
      letterDropEndsAt =
        time +
        LETTER_DROP_MIN_DURATION_S +
        hash(dropSeed) * (LETTER_DROP_MAX_DURATION_S - LETTER_DROP_MIN_DURATION_S)
      dropSeed += 1
      faceMaterial.emissiveMap = deadLetterTexture
    } else if (letterDropEndsAt >= 0 && time >= letterDropEndsAt) {
      letterDropEndsAt = -1
      nextLetterDropAt =
        time +
        LETTER_DROP_MIN_INTERVAL_S +
        hash(dropSeed) * (LETTER_DROP_MAX_INTERVAL_S - LETTER_DROP_MIN_INTERVAL_S)
      dropSeed += 1
      faceMaterial.emissiveMap = healthyTexture
    }
  }

  return { group, update }
}
